import { LinearProgress, Paper, Typography } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

import EnhancedTable, { PAGE_SIZE_OPTIONS } from '../WorkflowRunsPage/Table/EnhancedTable';
import FilterBar, { SORT_OPTIONS } from './FilterBar';
import ErrorSummaryDialog from './ErrorSummaryDialog';
import { useDashboardColumns } from './columns';

import useAutofetchedDashboardRunsAsync from 'store/useAutofetchedDashboardRunsAsync';
import useProjectsAsync from 'store/useProjectsAsync';
import { DashboardSortKey, DashboardSortOrder } from 'store/fetchDashboardRuns';
import useDebouncedSearchInput from 'hooks/useDebouncedSearchInput';
import useListStateParams, {
  booleanParam,
  oneOfParam,
  positiveIntParam,
  stringListParam,
  stringParam,
} from 'hooks/useListStateParams';

import IProject from 'types/IProject';
import IRunEntry from 'types/IRunEntry';

const listParams = {
  page: positiveIntParam(1),
  pageSize: oneOfParam(PAGE_SIZE_OPTIONS, 25),
  search: stringParam(),
  projects: stringListParam(),
  user: stringParam(),
  running: booleanParam(),
  sort: oneOfParam(
    SORT_OPTIONS.map((option) => option.key),
    'updated_on',
  ),
  order: oneOfParam<DashboardSortOrder>(['asc', 'desc'], 'desc'),
};

export default function Dashboard() {
  const { state, update } = useListStateParams(listParams);
  const [errorSummaryRun, setErrorSummaryRun] = useState<IRunEntry | null>(null);

  const applySearch = useCallback((value: string) => update({ search: value, page: 1 }), [update]);
  const [search, handleSearchChange] = useDebouncedSearchInput(state.search, applySearch);

  const projectsAsync = useProjectsAsync();
  const projects =
    projectsAsync.status === 'SUCCESS' || projectsAsync.status === 'REFRESH' ? projectsAsync.data : undefined;
  const selectedProjects = useMemo(
    () => (projects ?? []).filter((project) => state.projects.includes(project.id)),
    [projects, state.projects],
  );

  const handleProjectsChange = useCallback(
    (selected: IProject[]) => update({ projects: selected.map((project) => project.id), page: 1 }),
    [update],
  );

  const handleUserIdChange = useCallback((id: string) => update({ user: id, page: 1 }), [update]);

  const handleHasRunningTasksChange = useCallback((value: boolean) => update({ running: value, page: 1 }), [update]);

  const handleSortChange = useCallback((key: DashboardSortKey) => update({ sort: key, page: 1 }), [update]);

  const handleOrderChange = useCallback((value: DashboardSortOrder) => update({ order: value, page: 1 }), [update]);

  const runsAsync = useAutofetchedDashboardRunsAsync({
    page: state.page,
    pageSize: state.pageSize,
    search: state.search || undefined,
    projectIds: state.projects.length > 0 ? state.projects : undefined,
    userId: state.user || undefined,
    hasRunningTasks: state.running || undefined,
    sort: state.sort,
    order: state.order,
  });

  const { status } = runsAsync;
  const paginatedData = runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH' ? runsAsync.data : undefined;
  const runs = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;

  const columns = useDashboardColumns(setErrorSummaryRun);

  const handlePageChange = useCallback((newPage: number) => update({ page: newPage }), [update]);

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => update({ pageSize: newPageSize, page: 1 }),
    [update],
  );

  return (
    <section>
      <Paper elevation={1} style={{ padding: 48 }}>
        <Typography component="h1" variant="h6">
          Dashboard
        </Typography>
        <br />
        {status === 'ERROR' && <p>Something went wrong</p>}
        <FilterBar
          search={search}
          onSearchChange={handleSearchChange}
          selectedProjects={selectedProjects}
          onSelectedProjectsChange={handleProjectsChange}
          userId={state.user}
          onUserIdChange={handleUserIdChange}
          hasRunningTasks={state.running}
          onHasRunningTasksChange={handleHasRunningTasksChange}
          sort={state.sort}
          onSortChange={handleSortChange}
          order={state.order}
          onOrderChange={handleOrderChange}
        />
        <br />
        {['INIT', 'PENDING'].includes(status) && <LinearProgress />}
        <EnhancedTable<IRunEntry>
          columns={columns}
          items={runs}
          serverPagination={{
            page: state.page,
            pageSize: state.pageSize,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
      </Paper>
      {errorSummaryRun && <ErrorSummaryDialog run={errorSummaryRun} onClose={() => setErrorSummaryRun(null)} />}
    </section>
  );
}
